'use client'
import { deleteAllCookies } from '@/components/marketing-landing-page/utils'
import apiClient from '@/lib/api/api-client'
import storage from '@/utils/storage'
import { getLocalStorageValue } from '@/utils/utils'
import { useState } from 'react'
import uuid from 'react-uuid'

const baseUrl = process.env.NEXT_PUBLIC_PP_API_BASE_URL
const orgId = process.env.NEXT_PUBLIC_PWLIVE_ORG_ID
const clientSecret = process.env.NEXT_PUBLIC_CLIENT_SECRET

type Props = {
  handleGoogleLogin: any
}

export interface LoginResponse {
  success: boolean
  data?: {
    access_token: string
    refresh_token: string
    expires_in: number
    tokenId: string
    user: any
  }
  message?: string // Optional message property
  error?: {
    message: string
    status: number
  }
}

export const login = async ({
  selectedOrg,
  username,
  password,
}: {
  selectedOrg: string
  username: string
  password: string
}): Promise<LoginResponse> => {
  return await getToken(selectedOrg, username, password)
}

const getToken = async (selectedOrg: string, userName: string, password: string): Promise<LoginResponse> => {
  const body: any = {
    client_id: 'system-admin',
    client_secret: clientSecret,
    grant_type: 'password',
    organizationId: selectedOrg,
    username: `${userName}`,
    password: `${password}`,
  }

  try {
    const response: LoginResponse = await apiClient.post('PP_API', `${baseUrl}/v3/oauth/token`, body)

    const data = response.data

    if (selectedOrg !== null) {
      storage.set('organizationId', selectedOrg)
    }

    if (data?.access_token) {
      storage.set('TOKEN', data.access_token)
    }
    if (data?.user) {
      storage.set('USER', data.user)
      storage.set('USERID', data.user.id)
    }
    if (data?.refresh_token) {
      storage.set('REFRESH_TOKEN', data.refresh_token)
    }
    if (data?.expires_in) {
      storage.set('EXPIRE_IN', data.expires_in)
    }

    return {
      success: true,
      data,
      message: 'Login successful',
    }
  } catch (error: any) {
    if (error.message) {
      return {
        success: false,
        message: error.message,
      }
    } else {
      return {
        success: false,
        error: { message: 'Unexpected error occurred.', status: 500 },
        message: 'Unexpected error occurred.',
      }
    }
  }
}

export const verifyToken = async () => {
  const token = storage.get('TOKEN')
  if (token) {
    return true
  }
  return false
}

export const setAccessToken = (access_token: string) => {
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    localStorage.setItem('TOKEN', access_token)
  }
}

export const getAccessToken = () => {
  return storage.get('TOKEN')
}

export const refreshToken = async (shouldNotRedirect?: boolean) => {
  const refreshToken = storage.get('REFRESH_TOKEN')
  const organizationId = storage.get('organizationId')
  const body: any = {
    refresh_token: `${refreshToken}`,
    client_id: `${organizationId}`,
  }
  try {
    const response: any = await fetch(`${baseUrl}/v3/oauth/refresh-token`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
    })
    if (!response.ok) {
      if (shouldNotRedirect) {
        deleteAllCookies()
        localStorage.clear()
      } else {
        // window.location.href = '/admin/logout'
      }
      console.log('Failed to refresh token')
      throw new Error('Failed to refresh token')
    }
    const res = await response.json()
    if (res?.data?.access_token) {
      setAccessToken(res?.data?.access_token)
    }
    if (res?.data?.refresh_token && typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.setItem('REFRESH_TOKEN', res?.data?.refresh_token)
    }
    if (window?.PWWebSDK && window?.PWWebSDK?.setTokenContext) {
      window.PWWebSDK.setTokenContext = {
        token: res?.data?.access_token,
        refresh_token: res?.data?.refresh_token,
        expires_in: res?.data?.expires_in,
        randomId: getLocalStorageValue('randomId') || uuid(),
      }
    }
    return res?.data?.access_token
  } catch (error) {
    console.error('Error refreshing token:', error)
    throw error
  }
}

export const AuthService = (): Props => {
  const [error, setError] = useState('')

  const setUserDetails = async (response: any, username: string) => {
    // CookieService.setValue(CookieKeys.TOKEN_CONTEXT, {
    //   token: response?.data?.access_token,
    //   username,
    //   refresh_token: response?.data?.refresh_token,
    //   expires_in: response?.data?.expires_in,
    // });
    storage.set('TOKEN', response?.data?.access_token)
    if (username) {
      storage.set('USERNAME', username)
    }
    storage.set('REFRESH_TOKEN', response?.data?.refresh_token)
    storage.set('EXPIRE_IN', response?.data?.expires_in)
    if (response?.data?.user) {
      storage.set('USER', response?.data?.user)
      storage.set('USERID', response?.data?.user.id)
    } else {
      setError('Error!')
      console.log(error, '123456')
    }
  }

  const handleGoogleLogin = async (data: any, selectedOrg: string) => {
    try {
      setError('')
      const response: any = await apiClient.post('PP_API', `${baseUrl}/v3/oauth/social-connect`, data)
      if (response?.data?.user) {
        setUserDetails(response, '')
        return true
      }
    } catch (error: any) {
      if (error.message) {
        return {
          success: false,
          message: error.message,
        }
      } else {
        return {
          success: false,
          error: { message: 'Unexpected error occurred.', status: 500 },
          message: 'Unexpected error occurred.',
        }
      }
    }
  }

  return {
    handleGoogleLogin,
  }
}
